import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { DateFormatsEnum } from '../../../constants/date';
import {
  EXPIRATION_DATE_ERROR_TOOLTIP_TEXT,
  GIFT_BUDGET_TOOLTIP_TEXT,
  NO_EXPIRATION_DATE_LABEL,
  ONE_LINK_SUMMARY_LABELS,
} from '../../../constants/oneLink';
import { EXPIRATION_DATE } from '../../../constants/shipping';
import { IOneLinkErrors, OneLinkChunksEnum } from '../../../contexts/OneLinkSummaryProvider';
import { selectFulfillmentCenterItems } from '../../../store/selectors/fulfillmentCenters';
import { selectIsProductMarketplaceEnabled } from '../../../store/selectors/organizations';
import { selectUsers } from '../../../store/selectors/users';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { formatDate, formatUtcDateForDisplayInUserTimezone, isValidDate } from '../../../utils/date';
import { getFullName } from '../../../utils/helpers';
import { getItemSourceNote } from '../../../utils/itemSource';
import { getOneLinkStatusAndDate } from '../../../utils/oneLink';
import { calculateDaysLeft, getReportGiftBudget, getReportItemSources } from '../../../utils/reports';
import OneLinkStatus from '../../OneLinksComponent/components/OneLinkStatus';
import ReportItemSource from '../../ReportsComponent/components/ReportItemSource';
import { CopyableButton } from '../../_mui';
import { Section } from './common';
import { cellLabelSx, cellSubSx, cellValueSx } from './SummarySection.styles';

const EMPTY_VALUE = '—';

const metaLabelSx = { color: 'text.secondary' } as const;

interface ICellProps {
  label: React.ReactNode;
  value: React.ReactNode;
  valueTitle?: string;
  sub?: React.ReactNode;
  subTitle?: string;
  isValueMuted?: boolean;
}

const SummaryCell: React.FC<ICellProps> = ({ label, value, valueTitle, sub, subTitle, isValueMuted }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={cellLabelSx}>{label}</Box>
    <Box sx={{ ...cellValueSx, ...(isValueMuted ? { color: 'text.disabled' } : {}) }} title={valueTitle}>
      {value}
    </Box>
    {sub ? (
      <Box sx={cellSubSx} title={subTitle}>
        {sub}
      </Box>
    ) : null}
  </Box>
);

interface IProps {
  instance: IOneLinkWithCampaignSummaries | null | undefined;
  reportMode: boolean;
  actions?: { title: string; onClick?: () => void; disabled?: boolean }[];
  errors?: IOneLinkErrors;
}

export const SummarySection: React.FC<IProps> = ({ reportMode, errors, instance, actions }) => {
  const users = useSelector(selectUsers);
  const fulfillmentCenters = useSelector(selectFulfillmentCenterItems);
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);

  const expirationDateError = useMemo(() => errors?.[OneLinkChunksEnum.SummaryInfo]?.expiration_date, [errors]);
  const title = useMemo(() => instance?.title || 'No title', [instance]);

  const createdByUser = useMemo(() => {
    const user = users?.find((person) => person.email === instance?.created_by);
    return user ? getFullName(user.first_name, user.last_name) : '';
  }, [users, instance?.created_by]);

  const createdBy = useMemo(() => createdByUser || instance?.created_by || '', [createdByUser, instance?.created_by]);

  const sender = useMemo(
    () => getFullName(instance?.sender_first_name, instance?.sender_last_name),
    [instance?.sender_first_name, instance?.sender_last_name],
  );

  const { createdAtDate } = useMemo(() => {
    if (reportMode && instance) {
      return getOneLinkStatusAndDate(instance);
    }

    return { createdAtDate: '' };
  }, [reportMode, instance]);

  const createdAt = useMemo(
    () => (createdAtDate ? formatDate(createdAtDate, DateFormatsEnum.ddMMMyyyy) : ''),
    [createdAtDate],
  );

  const redemptions = useMemo(() => {
    const confirmed = instance?.use_times_count ?? 0;
    const limit = instance?.max_submit_count;

    if (!limit) {
      return { value: `${confirmed} confirmed`, sub: '' };
    }

    return { value: `${confirmed} of ${limit} confirmed`, sub: `${Math.max(0, limit - confirmed)} left` };
  }, [instance?.use_times_count, instance?.max_submit_count]);

  const expiration = useMemo(() => {
    const rawDate = instance?.[EXPIRATION_DATE];

    if (!rawDate || !isValidDate(new Date(rawDate))) {
      return { value: NO_EXPIRATION_DATE_LABEL, sub: '' };
    }

    return { value: formatUtcDateForDisplayInUserTimezone(rawDate), sub: calculateDaysLeft(rawDate) };
  }, [instance]);

  const itemSources = useMemo(() => getReportItemSources(instance), [instance]);
  const giftBudget = useMemo(() => getReportGiftBudget(instance), [instance]);

  const warehouseNames = useMemo(() => {
    const centerNameById = new Map((fulfillmentCenters ?? []).map((center) => [center.uid, center.name]));
    const campaignItems = (instance?.pick_campaigns ?? []).flatMap((campaign) => campaign.items ?? []);

    return Array.from(
      new Set(
        [...(instance?.items ?? []), ...campaignItems]
          .map((item) => centerNameById.get(item.fulfillment_center_id))
          .filter((name): name is string => !!name),
      ),
    ).join(', ');
  }, [fulfillmentCenters, instance?.items, instance?.pick_campaigns]);

  const itemSourceSub = useMemo(() => getItemSourceNote(itemSources, warehouseNames), [itemSources, warehouseNames]);

  const headerTitle = useMemo(
    () => (
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Typography
            component="div"
            title={title}
            sx={{ fontSize: 20, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
          {instance?.status ? <OneLinkStatus status={instance.status} /> : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, fontSize: 14, minWidth: 0 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box component="span" sx={metaLabelSx}>
              {ONE_LINK_SUMMARY_LABELS.createdBy}
            </Box>
            <Box component="span" title={createdBy} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {createdBy}
            </Box>
            {createdAt ? <Box component="span" sx={metaLabelSx}>{`· ${createdAt}`}</Box> : null}
          </Box>
          <Divider orientation="vertical" flexItem sx={{ height: 14, alignSelf: 'center' }} />
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box component="span" sx={metaLabelSx}>
              {ONE_LINK_SUMMARY_LABELS.sender}
            </Box>
            <Box component="span" title={sender} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {sender}
            </Box>
          </Box>
        </Box>
      </Box>
    ),
    [title, instance?.status, createdBy, createdAt, sender],
  );

  const headerActions = useMemo(
    () =>
      instance?.url ? (
        <CopyableButton value={instance.url} toastLabel="Claim URL">
          <Button component="span" variant="outlined" startIcon={<LinkIcon />}>
            {ONE_LINK_SUMMARY_LABELS.copyLink}
          </Button>
        </CopyableButton>
      ) : null,
    [instance?.url],
  );

  const giftBudgetLabel = useMemo(
    () => (
      <>
        {ONE_LINK_SUMMARY_LABELS.giftBudget}
        {giftBudget ? (
          <Tooltip title={GIFT_BUDGET_TOOLTIP_TEXT} arrow placement="top">
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
          </Tooltip>
        ) : null}
      </>
    ),
    [giftBudget],
  );

  return (
    <Section title={headerTitle} actions={actions} headerActions={headerActions} titleAlignment="flex-start">
      <Divider sx={{ mb: 2.5 }} />
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <SummaryCell
          label={ONE_LINK_SUMMARY_LABELS.redemptions}
          value={redemptions.value}
          valueTitle={redemptions.value}
          sub={redemptions.sub}
          subTitle={redemptions.sub}
        />
        <SummaryCell
          label={ONE_LINK_SUMMARY_LABELS.expirationDate}
          value={expiration.value}
          valueTitle={expirationDateError ? EXPIRATION_DATE_ERROR_TOOLTIP_TEXT : expiration.value}
          sub={expiration.sub}
          subTitle={expiration.sub}
        />
        {isMarketplaceEnabled ? (
          <SummaryCell
            label={ONE_LINK_SUMMARY_LABELS.itemSource}
            value={<ReportItemSource sources={itemSources} />}
            sub={itemSourceSub}
            subTitle={itemSourceSub}
          />
        ) : null}
        {isMarketplaceEnabled ? (
          <SummaryCell
            label={giftBudgetLabel}
            value={giftBudget ? `$${giftBudget.value.toLocaleString()} per recipient` : EMPTY_VALUE}
            isValueMuted={!giftBudget}
          />
        ) : null}
      </Box>
    </Section>
  );
};
